/**
 * VIPMatchupPanel Component
 *
 * Premium insight-driven matchup intelligence panel for Rockets games.
 * Replaces SportsStatsWidget with curated, conversational content.
 *
 * Data Sources:
 * - BallDontLie API: Team records, standings, recent games, player stats
 * - The Odds API: Vegas spread and over/under
 *
 * Features:
 * - Team records (overall + home/away)
 * - Vegas spread and O/U
 * - Last 10 games trend
 * - Top 3 players by PPG and RPG
 * - Auto-generated date-aware narrative (Today/Tomorrow/Day of Week/Date)
 *
 * Style: Dark glass card with gold accent (#C6A664)
 * Theme Support: Adapts to dark/light/default/dynamic themes
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Users, Target, BarChart3 } from 'lucide-react';
import { getCleanEventName, getExpandedOpponentName } from '../utils/attendanceParser';

// Gold accent color for VIP styling
// This works across all themes (dark, light, default, dynamic)
const GOLD_ACCENT = '#C6A664';
const GOLD_ACCENT_MUTED = 'rgba(198, 166, 100, 0.6)';

// Theme-aware CSS variable fallbacks
// These ensure the component looks good in all appearance modes
const themeStyles = {
  // Background adapts to theme
  containerBg: {
    dark: 'linear-gradient(135deg, rgba(30, 30, 35, 0.95) 0%, rgba(20, 20, 25, 0.98) 100%)',
    light: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(245, 245, 245, 0.98) 100%)',
  },
  // Border colors
  borderColor: {
    dark: 'rgba(255, 255, 255, 0.1)',
    light: 'rgba(0, 0, 0, 0.1)',
  },
  // Glow effect
  goldGlow: {
    dark: 'rgba(198, 166, 100, 0.08)',
    light: 'rgba(198, 166, 100, 0.12)',
  },
};

// ============================================
// SVG Icon Components (Scalable, Professional)
// ============================================

interface IconProps {
  size?: number;
  className?: string;
}

/**
 * Hot Streak Icon - Fire/flame indicator for winning streaks
 */
const HotStreakIcon: React.FC<IconProps> = ({ size = 16, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-label="Hot streak"
  >
    <path
      d="M12 2C12 2 8 6 8 10C8 12.21 9.79 14 12 14C14.21 14 16 12.21 16 10C16 6 12 2 12 2Z"
      fill="currentColor"
      fillOpacity="0.6"
    />
    <path
      d="M12 22C16.4183 22 20 18.4183 20 14C20 9.58172 12 2 12 2C12 2 4 9.58172 4 14C4 18.4183 7.58172 22 12 22Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 18C13.6569 18 15 16.6569 15 15C15 13.3431 12 10 12 10C12 10 9 13.3431 9 15C9 16.6569 10.3431 18 12 18Z"
      fill="currentColor"
    />
  </svg>
);

/**
 * Cold Streak Icon - Snowflake/ice indicator for losing streaks
 */
const ColdStreakIcon: React.FC<IconProps> = ({ size = 16, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-label="Cold streak"
  >
    <path
      d="M12 2V22M12 2L8 6M12 2L16 6M12 22L8 18M12 22L16 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M4.93 4.93L19.07 19.07M4.93 4.93L7.76 8.34M4.93 4.93L8.34 7.76M19.07 19.07L15.66 16.24M19.07 19.07L16.24 15.66"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M4.93 19.07L19.07 4.93M4.93 19.07L8.34 16.24M4.93 19.07L7.76 15.66M19.07 4.93L15.66 7.76M19.07 4.93L16.24 8.34"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="12" r="3" fill="currentColor" fillOpacity="0.4" />
  </svg>
);

/**
 * Stats Icon - Chart/analytics indicator
 */
const StatsIcon: React.FC<IconProps> = ({ size = 16, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-label="Statistics"
  >
    <rect x="3" y="10" width="4" height="11" rx="1" fill="currentColor" fillOpacity="0.6" />
    <rect x="10" y="3" width="4" height="18" rx="1" fill="currentColor" />
    <rect x="17" y="7" width="4" height="14" rx="1" fill="currentColor" fillOpacity="0.8" />
  </svg>
);

/**
 * High Scoring Icon - Upward trend/high-octane game indicator
 */
const HighScoringIcon: React.FC<IconProps> = ({ size = 16, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-label="High scoring expected"
  >
    <path
      d="M3 17L9 11L13 15L21 7"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M14 7H21V14"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="9" cy="11" r="2" fill="currentColor" fillOpacity="0.4" />
    <circle cx="13" cy="15" r="2" fill="currentColor" fillOpacity="0.4" />
  </svg>
);

/**
 * Low Scoring Icon - Downward trend/defensive game indicator
 */
const LowScoringIcon: React.FC<IconProps> = ({ size = 16, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-label="Low scoring expected"
  >
    <path
      d="M3 7L9 13L13 9L21 17"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M14 17H21V10"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="9" cy="13" r="2" fill="currentColor" fillOpacity="0.4" />
    <circle cx="13" cy="9" r="2" fill="currentColor" fillOpacity="0.4" />
  </svg>
);

// ============================================
// Date Formatting Utilities
// ============================================

/**
 * Format event date into human-readable label
 * Returns: "Today", "Tomorrow", day name for this week, or formatted date
 */
const formatEventDateLabel = (eventDate?: string): { label: string; isToday: boolean; isTomorrow: boolean; dayName: string } => {
  if (!eventDate) {
    return { label: 'This Game', isToday: false, isTomorrow: false, dayName: '' };
  }

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const eventDateObj = new Date(eventDate + 'T00:00:00'); // Parse as local date
  const eventDay = new Date(eventDateObj.getFullYear(), eventDateObj.getMonth(), eventDateObj.getDate());

  const diffDays = Math.round((eventDay.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const dayName = dayNames[eventDay.getDay()];

  if (diffDays === 0) {
    return { label: 'Tonight', isToday: true, isTomorrow: false, dayName };
  } else if (diffDays === 1) {
    return { label: 'Tomorrow', isToday: false, isTomorrow: true, dayName };
  } else if (diffDays > 1 && diffDays <= 6) {
    return { label: dayName, isToday: false, isTomorrow: false, dayName };
  } else {
    // Format as "Jan 15" or "Jan 15, 2025" if different year
    const month = eventDateObj.toLocaleDateString('en-US', { month: 'short' });
    const day = eventDateObj.getDate();
    const year = eventDateObj.getFullYear();
    const currentYear = now.getFullYear();

    if (year === currentYear) {
      return { label: `${month} ${day}`, isToday: false, isTomorrow: false, dayName };
    } else {
      return { label: `${month} ${day}, ${year}`, isToday: false, isTomorrow: false, dayName };
    }
  }
};

// NBA team mapping for BallDontLie API
const NBA_TEAM_IDS: Record<string, { id: number; name: string; abbreviation: string; city: string }> = {
  rockets: { id: 11, name: 'Rockets', abbreviation: 'HOU', city: 'Houston' },
  lakers: { id: 14, name: 'Lakers', abbreviation: 'LAL', city: 'Los Angeles' },
  warriors: { id: 10, name: 'Warriors', abbreviation: 'GSW', city: 'Golden State' },
  suns: { id: 24, name: 'Suns', abbreviation: 'PHX', city: 'Phoenix' },
  clippers: { id: 13, name: 'Clippers', abbreviation: 'LAC', city: 'Los Angeles' },
  mavericks: { id: 7, name: 'Mavericks', abbreviation: 'DAL', city: 'Dallas' },
  mavs: { id: 7, name: 'Mavericks', abbreviation: 'DAL', city: 'Dallas' },
  spurs: { id: 27, name: 'Spurs', abbreviation: 'SAS', city: 'San Antonio' },
  nuggets: { id: 8, name: 'Nuggets', abbreviation: 'DEN', city: 'Denver' },
  thunder: { id: 22, name: 'Thunder', abbreviation: 'OKC', city: 'Oklahoma City' },
  timberwolves: { id: 17, name: 'Timberwolves', abbreviation: 'MIN', city: 'Minnesota' },
  wolves: { id: 17, name: 'Timberwolves', abbreviation: 'MIN', city: 'Minnesota' },
  pelicans: { id: 19, name: 'Pelicans', abbreviation: 'NOP', city: 'New Orleans' },
  grizzlies: { id: 15, name: 'Grizzlies', abbreviation: 'MEM', city: 'Memphis' },
  jazz: { id: 29, name: 'Jazz', abbreviation: 'UTA', city: 'Utah' },
  kings: { id: 26, name: 'Kings', abbreviation: 'SAC', city: 'Sacramento' },
  blazers: { id: 25, name: 'Trail Blazers', abbreviation: 'POR', city: 'Portland' },
  'trail blazers': { id: 25, name: 'Trail Blazers', abbreviation: 'POR', city: 'Portland' },
  celtics: { id: 2, name: 'Celtics', abbreviation: 'BOS', city: 'Boston' },
  bucks: { id: 16, name: 'Bucks', abbreviation: 'MIL', city: 'Milwaukee' },
  heat: { id: 16, name: 'Heat', abbreviation: 'MIA', city: 'Miami' },
  sixers: { id: 23, name: '76ers', abbreviation: 'PHI', city: 'Philadelphia' },
  '76ers': { id: 23, name: '76ers', abbreviation: 'PHI', city: 'Philadelphia' },
  knicks: { id: 20, name: 'Knicks', abbreviation: 'NYK', city: 'New York' },
  nets: { id: 4, name: 'Nets', abbreviation: 'BKN', city: 'Brooklyn' },
  cavaliers: { id: 6, name: 'Cavaliers', abbreviation: 'CLE', city: 'Cleveland' },
  cavs: { id: 6, name: 'Cavaliers', abbreviation: 'CLE', city: 'Cleveland' },
  bulls: { id: 5, name: 'Bulls', abbreviation: 'CHI', city: 'Chicago' },
  hawks: { id: 1, name: 'Hawks', abbreviation: 'ATL', city: 'Atlanta' },
  raptors: { id: 28, name: 'Raptors', abbreviation: 'TOR', city: 'Toronto' },
  hornets: { id: 30, name: 'Hornets', abbreviation: 'CHA', city: 'Charlotte' },
  wizards: { id: 30, name: 'Wizards', abbreviation: 'WAS', city: 'Washington' },
  magic: { id: 21, name: 'Magic', abbreviation: 'ORL', city: 'Orlando' },
  pistons: { id: 9, name: 'Pistons', abbreviation: 'DET', city: 'Detroit' },
  pacers: { id: 12, name: 'Pacers', abbreviation: 'IND', city: 'Indiana' },
};

// Types
interface TeamStats {
  wins: number;
  losses: number;
  homeWins: number;
  homeLosses: number;
  awayWins: number;
  awayLosses: number;
  last10: ('W' | 'L')[];
  streak: { type: 'W' | 'L'; count: number };
}

interface PlayerStats {
  name: string;
  ppg: number;
  rpg: number;
  apg: number;
}

interface OddsData {
  spread: number;
  spreadFavorite: 'home' | 'away';
  overUnder: number;
}

interface MatchupData {
  homeTeam: {
    name: string;
    abbreviation: string;
    stats: TeamStats | null;
    topPlayers: PlayerStats[];
  };
  awayTeam: {
    name: string;
    abbreviation: string;
    stats: TeamStats | null;
    topPlayers: PlayerStats[];
  };
  odds: OddsData | null;
  narrative: NarrativeInsight[];
  isLoading: boolean;
  error: string | null;
}

interface VIPMatchupPanelProps {
  eventType: string;
  opponent?: string | null;
  eventDate?: string;
  className?: string;
}

/**
 * Find opponent team from name
 */
const findOpponentTeam = (opponentName: string | null | undefined): { id: number; name: string; abbreviation: string; city: string } | null => {
  if (!opponentName) return null;

  const expandedName = getExpandedOpponentName(opponentName);
  const searchName = (expandedName || getCleanEventName(opponentName) || opponentName).toLowerCase().trim();

  for (const [key, team] of Object.entries(NBA_TEAM_IDS)) {
    if (
      searchName.includes(key) ||
      team.name.toLowerCase().includes(searchName) ||
      searchName.includes(team.name.toLowerCase()) ||
      searchName.includes(team.city.toLowerCase())
    ) {
      return team;
    }
  }
  return null;
};

/**
 * Shimmer loading placeholder
 */
const ShimmerLoader: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`animate-pulse bg-gradient-to-r from-white/5 via-white/10 to-white/5 rounded ${className}`} />
);

/**
 * Trend indicator component with professional SVG icons
 */
const TrendIndicator: React.FC<{ trend: ('W' | 'L')[] }> = ({ trend }) => {
  const wins = trend.filter(r => r === 'W').length;
  const losses = trend.filter(r => r === 'L').length;
  const isHot = wins >= 7;
  const isCold = losses >= 7;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs text-dark-text">Last 10</span>
        <div className="flex items-center gap-1">
          {isHot && <HotStreakIcon size={14} className="text-orange-400" />}
          {isCold && <ColdStreakIcon size={14} className="text-blue-400" />}
          <span className="text-sm font-semibold" style={{ color: GOLD_ACCENT }}>
            {wins}-{losses}
          </span>
        </div>
      </div>
      <div className="flex gap-0.5">
        {trend.map((result, i) => (
          <div
            key={i}
            className={`w-2.5 h-2.5 rounded-sm ${
              result === 'W' ? 'bg-green-500' : 'bg-red-500'
            }`}
            title={`Game ${i + 1}: ${result === 'W' ? 'Win' : 'Loss'}`}
          />
        ))}
      </div>
    </div>
  );
};

/**
 * Player stat row
 */
const PlayerStatRow: React.FC<{ player: PlayerStats; rank: number; statType: 'ppg' | 'rpg' }> = ({ player, rank, statType }) => {
  const statValue = statType === 'ppg' ? player.ppg : player.rpg;
  const statLabel = statType === 'ppg' ? 'PPG' : 'RPG';

  return (
    <div className="flex items-center justify-between py-1.5">
      <div className="flex items-center gap-2">
        <span className="text-xs text-dark-text w-4">{rank}.</span>
        <span className="text-sm text-white truncate max-w-[120px]">{player.name}</span>
      </div>
      <div className="flex items-center gap-1">
        <span className="text-sm font-semibold" style={{ color: GOLD_ACCENT }}>
          {statValue.toFixed(1)}
        </span>
        <span className="text-xs text-dark-text">{statLabel}</span>
      </div>
    </div>
  );
};

/**
 * Narrative insight markers (rendered as components, not text emojis)
 */
interface NarrativeInsight {
  text: string;
  icon?: 'stats' | 'hot' | 'cold' | 'high' | 'low';
}

/**
 * Generate narrative based on matchup data with date-aware language
 */
const generateNarrative = (
  homeTeam: { name: string; stats: TeamStats | null },
  awayTeam: { name: string; stats: TeamStats | null },
  odds: OddsData | null,
  dateInfo: { label: string; isToday: boolean; isTomorrow: boolean }
): NarrativeInsight[] => {
  const insights: NarrativeInsight[] = [];

  // Determine the time reference phrase
  const timePhrase = dateInfo.isToday
    ? 'tonight'
    : dateInfo.isTomorrow
      ? 'tomorrow'
      : `on ${dateInfo.label}`;

  const thisGamePhrase = dateInfo.isToday
    ? 'this one'
    : dateInfo.isTomorrow
      ? 'tomorrow\'s game'
      : `the ${dateInfo.label} matchup`;

  // Team performance intro
  if (homeTeam.stats && awayTeam.stats) {
    const homeWinPct = homeTeam.stats.wins / (homeTeam.stats.wins + homeTeam.stats.losses);
    const awayWinPct = awayTeam.stats.wins / (awayTeam.stats.wins + awayTeam.stats.losses);

    if (homeWinPct > awayWinPct + 0.15) {
      insights.push({ text: `The Rockets come into ${thisGamePhrase} as the stronger team on paper` });
    } else if (awayWinPct > homeWinPct + 0.15) {
      insights.push({ text: `Houston faces a tough test against a ${awayTeam.name} squad playing well` });
    } else {
      insights.push({ text: `This shapes up as an evenly matched battle` });
    }

    // Home court advantage
    if (homeTeam.stats.homeWins > homeTeam.stats.homeLosses * 1.5) {
      insights.push({ text: `The home cooking at Toyota Center has been good to the Rockets this season` });
    }

    // Streak analysis
    const homeStreak = homeTeam.stats.streak;
    const awayStreak = awayTeam.stats.streak;

    if (homeStreak.count >= 3 && homeStreak.type === 'W') {
      insights.push({ text: `Houston is rolling with ${homeStreak.count} straight wins`, icon: 'hot' });
    } else if (homeStreak.count >= 3 && homeStreak.type === 'L') {
      insights.push({ text: `The Rockets look to snap a ${homeStreak.count}-game skid`, icon: 'cold' });
    }

    if (awayStreak.count >= 3 && awayStreak.type === 'W') {
      insights.push({ text: `Watch out—${awayTeam.name} arrives on a ${awayStreak.count}-game heater`, icon: 'hot' });
    }
  }

  // Vegas insight (no betting language)
  if (odds) {
    const spread = Math.abs(odds.spread);
    const favorite = odds.spreadFavorite === 'home' ? 'Rockets' : awayTeam.name;

    if (spread <= 3) {
      insights.push({ text: `Vegas sees this as a toss-up—expect a close one`, icon: 'stats' });
    } else if (spread >= 10) {
      insights.push({ text: `The numbers heavily favor ${favorite} ${timePhrase}`, icon: 'stats' });
    } else {
      insights.push({ text: `${favorite} is the slight favorite according to the oddsmakers`, icon: 'stats' });
    }

    if (odds.overUnder >= 230) {
      insights.push({ text: `This has the makings of a high-scoring affair`, icon: 'high' });
    } else if (odds.overUnder <= 210) {
      insights.push({ text: `Defense could be the story—projected to be a grind-it-out game`, icon: 'low' });
    }
  }

  return insights;
};

/**
 * Render narrative insight icon
 */
const NarrativeIcon: React.FC<{ type?: 'stats' | 'hot' | 'cold' | 'high' | 'low' }> = ({ type }) => {
  if (!type) return null;

  switch (type) {
    case 'stats':
      return <StatsIcon size={14} className="text-dark-text inline-block mr-1 -mt-0.5" />;
    case 'hot':
      return <HotStreakIcon size={14} className="text-orange-400 inline-block mr-1 -mt-0.5" />;
    case 'cold':
      return <ColdStreakIcon size={14} className="text-blue-400 inline-block mr-1 -mt-0.5" />;
    case 'high':
      return <HighScoringIcon size={14} className="text-green-400 inline-block mr-1 -mt-0.5" />;
    case 'low':
      return <LowScoringIcon size={14} className="text-amber-400 inline-block mr-1 -mt-0.5" />;
    default:
      return null;
  }
};

/**
 * Mock data fetcher (replace with real API calls in production)
 */
const fetchMatchupData = async (
  _homeTeamId: number,
  awayTeamId: number | null,
  awayTeamName: string
): Promise<Partial<MatchupData>> => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 1200));

  // Generate semi-realistic mock data
  const generateStats = (isHome: boolean): TeamStats => {
    const wins = Math.floor(Math.random() * 30) + 15;
    const losses = Math.floor(Math.random() * 25) + 10;
    const homeWins = Math.floor(wins * 0.6);
    const homeLosses = Math.floor(losses * 0.4);

    const last10: ('W' | 'L')[] = [];
    for (let i = 0; i < 10; i++) {
      last10.push(Math.random() > 0.45 ? 'W' : 'L');
    }

    // Calculate streak from last10
    let streakType: 'W' | 'L' = last10[0];
    let streakCount = 1;
    for (let i = 1; i < last10.length; i++) {
      if (last10[i] === streakType) {
        streakCount++;
      } else {
        break;
      }
    }

    return {
      wins,
      losses,
      homeWins,
      homeLosses,
      awayWins: wins - homeWins,
      awayLosses: losses - homeLosses,
      last10,
      streak: { type: streakType, count: streakCount },
    };
  };

  const generatePlayers = (): PlayerStats[] => {
    const names = [
      'Jalen Green', 'Alperen Sengun', 'Fred VanVleet', 'Jabari Smith Jr.', 'Dillon Brooks',
      'Amen Thompson', 'Tari Eason', 'Cam Whitmore', 'Steven Adams', 'Jeff Green',
    ];
    const shuffled = names.sort(() => Math.random() - 0.5).slice(0, 3);

    return shuffled.map((name, i) => ({
      name,
      ppg: parseFloat((25 - i * 5 + Math.random() * 5).toFixed(1)),
      rpg: parseFloat((10 - i * 2 + Math.random() * 3).toFixed(1)),
      apg: parseFloat((6 - i + Math.random() * 2).toFixed(1)),
    }));
  };

  const homeStats = generateStats(true);
  const awayStats = awayTeamId ? generateStats(false) : null;

  // Mock odds
  const spread = parseFloat((Math.random() * 12 - 6).toFixed(1));
  const odds: OddsData = {
    spread: Math.abs(spread),
    spreadFavorite: spread < 0 ? 'home' : 'away',
    overUnder: Math.floor(Math.random() * 30) + 210,
  };

  return {
    homeTeam: {
      name: 'Houston Rockets',
      abbreviation: 'HOU',
      stats: homeStats,
      topPlayers: generatePlayers(),
    },
    awayTeam: {
      name: awayTeamName,
      abbreviation: awayTeamId ? Object.values(NBA_TEAM_IDS).find(t => t.id === awayTeamId)?.abbreviation || 'OPP' : 'OPP',
      stats: awayStats,
      topPlayers: awayTeamId ? generatePlayers() : [],
    },
    odds,
    isLoading: false,
    error: null,
  };
};

/**
 * VIPMatchupPanel Component
 */
export const VIPMatchupPanel: React.FC<VIPMatchupPanelProps> = ({
  eventType,
  opponent,
  eventDate,
  className = '',
}) => {
  // Format event date for display
  const dateInfo = useMemo(() => formatEventDateLabel(eventDate), [eventDate]);

  const [matchupData, setMatchupData] = useState<MatchupData>({
    homeTeam: { name: 'Houston Rockets', abbreviation: 'HOU', stats: null, topPlayers: [] },
    awayTeam: { name: '', abbreviation: '', stats: null, topPlayers: [] },
    odds: null,
    narrative: [],
    isLoading: true,
    error: null,
  });

  // Determine if this is a Rockets game
  const isRocketsGame = useMemo(() => {
    const teamKey = eventType.toLowerCase();
    return (
      teamKey === 'rockets' ||
      opponent?.toLowerCase().includes('rockets') ||
      Object.keys(NBA_TEAM_IDS).some(key => opponent?.toLowerCase().includes(key))
    );
  }, [eventType, opponent]);

  // Find opponent team
  const opponentTeam = useMemo(() => findOpponentTeam(opponent), [opponent]);

  // Fetch data on mount
  useEffect(() => {
    if (!isRocketsGame) return;

    const fetchData = async () => {
      setMatchupData(prev => ({ ...prev, isLoading: true, error: null }));

      try {
        const data = await fetchMatchupData(
          NBA_TEAM_IDS.rockets.id,
          opponentTeam?.id || null,
          opponentTeam ? `${opponentTeam.city} ${opponentTeam.name}` : opponent || 'Opponent'
        );

        const narrative = generateNarrative(
          { name: 'Houston Rockets', stats: data.homeTeam?.stats || null },
          { name: data.awayTeam?.name || 'Opponent', stats: data.awayTeam?.stats || null },
          data.odds || null,
          dateInfo
        );

        setMatchupData({
          homeTeam: data.homeTeam || matchupData.homeTeam,
          awayTeam: data.awayTeam || matchupData.awayTeam,
          odds: data.odds || null,
          narrative,
          isLoading: false,
          error: null,
        });
      } catch (err) {
        setMatchupData(prev => ({
          ...prev,
          isLoading: false,
          error: 'Unable to load matchup data',
          narrative: [],
        }));
      }
    };

    fetchData();
  }, [isRocketsGame, opponentTeam, opponent, dateInfo]);

  // Don't render for non-Rockets games
  if (!isRocketsGame) {
    return null;
  }

  const { homeTeam, awayTeam, odds, narrative, isLoading, error } = matchupData;

  return (
    <div
      className={`
        relative overflow-hidden rounded-xl
        border border-white/10/10 light:border-black/10
        bg-gradient-to-br from-gray-900/95 to-gray-950/98
        backdrop-blur-xl
        ${className}
      `}
    >
      {/* Glass overlay effect - gold accent glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at top right, rgba(198, 166, 100, 0.08) 0%, transparent 60%)',
        }}
      />

      {/* Header - Date-aware title */}
      <div className="relative px-4 py-3 border-b border-white/10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Target size={18} style={{ color: GOLD_ACCENT }} />
            <h3 className="text-sm font-bold text-white tracking-wide uppercase">
              {dateInfo.isToday ? "Tonight's Matchup" : `${dateInfo.label}'s Matchup`}
            </h3>
          </div>
          <div className="flex items-center gap-1">
            <BarChart3 size={14} className="text-dark-text" />
            <span className="text-xs text-dark-text">VIP Insights</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="relative p-4 space-y-5">
        {error ? (
          <div className="text-center py-6">
            <p className="text-sm text-dark-text">{error}</p>
          </div>
        ) : (
          <>
            {/* Team Records */}
            <div className="grid grid-cols-2 gap-4">
              {/* Home Team (Rockets) */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <img
                    src="https://a.espncdn.com/i/teamlogos/nba/500/hou.png"
                    alt="Rockets"
                    className="w-8 h-8 object-contain"
                  />
                  <div>
                    <p className="text-sm font-semibold text-white">Rockets</p>
                    <p className="text-xs text-dark-text">Home</p>
                  </div>
                </div>

                {isLoading ? (
                  <div className="space-y-2">
                    <ShimmerLoader className="h-5 w-16" />
                    <ShimmerLoader className="h-4 w-20" />
                  </div>
                ) : homeTeam.stats ? (
                  <div className="space-y-1">
                    <p className="text-lg font-bold text-white">
                      {homeTeam.stats.wins}-{homeTeam.stats.losses}
                    </p>
                    <p className="text-xs text-dark-text">
                      Home: {homeTeam.stats.homeWins}-{homeTeam.stats.homeLosses}
                    </p>
                    <TrendIndicator trend={homeTeam.stats.last10} />
                  </div>
                ) : null}
              </div>

              {/* Away Team */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 justify-end">
                  <div className="text-right">
                    <p className="text-sm font-semibold text-white">
                      {opponentTeam?.name || opponent || 'Opponent'}
                    </p>
                    <p className="text-xs text-dark-text">Away</p>
                  </div>
                  <img
                    src={
                      opponentTeam
                        ? `https://a.espncdn.com/i/teamlogos/nba/500/${opponentTeam.abbreviation.toLowerCase()}.png`
                        : 'https://a.espncdn.com/i/teamlogos/default-team-logo-500.png'
                    }
                    alt={opponentTeam?.name || 'Opponent'}
                    className="w-8 h-8 object-contain"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = 'https://a.espncdn.com/i/teamlogos/default-team-logo-500.png';
                    }}
                  />
                </div>

                {isLoading ? (
                  <div className="space-y-2 flex flex-col items-end">
                    <ShimmerLoader className="h-5 w-16" />
                    <ShimmerLoader className="h-4 w-20" />
                  </div>
                ) : awayTeam.stats ? (
                  <div className="space-y-1 text-right">
                    <p className="text-lg font-bold text-white">
                      {awayTeam.stats.wins}-{awayTeam.stats.losses}
                    </p>
                    <p className="text-xs text-dark-text">
                      Away: {awayTeam.stats.awayWins}-{awayTeam.stats.awayLosses}
                    </p>
                    <div className="flex justify-end">
                      <TrendIndicator trend={awayTeam.stats.last10} />
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            {/* Vegas Line */}
            {(isLoading || odds) && (
              <div
                className="p-3 rounded-lg border"
                style={{
                  background: 'rgba(198, 166, 100, 0.08)',
                  borderColor: 'rgba(198, 166, 100, 0.2)',
                }}
              >
                <p className="text-xs text-dark-text mb-2 uppercase tracking-wide">The Line</p>
                {isLoading ? (
                  <div className="flex justify-between">
                    <ShimmerLoader className="h-5 w-24" />
                    <ShimmerLoader className="h-5 w-20" />
                  </div>
                ) : odds ? (
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-white">Spread:</span>
                      <span className="text-sm font-semibold" style={{ color: GOLD_ACCENT }}>
                        {odds.spreadFavorite === 'home' ? 'HOU' : awayTeam.abbreviation} -{odds.spread}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-white">O/U:</span>
                      <span className="text-sm font-semibold" style={{ color: GOLD_ACCENT }}>
                        {odds.overUnder}
                      </span>
                    </div>
                  </div>
                ) : null}
              </div>
            )}

            {/* Top Players */}
            <div className="grid grid-cols-2 gap-4">
              {/* Rockets Players */}
              <div>
                <p className="text-xs text-dark-text mb-2 uppercase tracking-wide flex items-center gap-1">
                  <Users size={12} />
                  Rockets Leaders
                </p>
                {isLoading ? (
                  <div className="space-y-2">
                    {[1, 2, 3].map(i => (
                      <ShimmerLoader key={i} className="h-6 w-full" />
                    ))}
                  </div>
                ) : (
                  <div className="space-y-0.5">
                    {homeTeam.topPlayers.slice(0, 3).map((player, i) => (
                      <PlayerStatRow key={player.name} player={player} rank={i + 1} statType="ppg" />
                    ))}
                  </div>
                )}
              </div>

              {/* Opponent Players */}
              <div>
                <p className="text-xs text-dark-text mb-2 uppercase tracking-wide flex items-center gap-1 justify-end">
                  <span>{opponentTeam?.name || 'Opponent'} Leaders</span>
                  <Users size={12} />
                </p>
                {isLoading ? (
                  <div className="space-y-2">
                    {[1, 2, 3].map(i => (
                      <ShimmerLoader key={i} className="h-6 w-full" />
                    ))}
                  </div>
                ) : awayTeam.topPlayers.length > 0 ? (
                  <div className="space-y-0.5">
                    {awayTeam.topPlayers.slice(0, 3).map((player, i) => (
                      <PlayerStatRow key={player.name} player={player} rank={i + 1} statType="ppg" />
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-dark-text text-right">Stats unavailable</p>
                )}
              </div>
            </div>

            {/* Narrative - Date-aware with SVG icons */}
            <div className="pt-3 border-t border-white/10">
              <p className="text-xs text-dark-text mb-2 uppercase tracking-wide">
                {dateInfo.isToday ? 'What to Expect Tonight' : `What to Expect ${dateInfo.label}`}
              </p>
              {isLoading ? (
                <div className="space-y-2">
                  <ShimmerLoader className="h-4 w-full" />
                  <ShimmerLoader className="h-4 w-3/4" />
                </div>
              ) : narrative.length > 0 ? (
                <div className="text-sm text-dark-text leading-relaxed space-y-1">
                  {narrative.map((insight, i) => (
                    <span key={i} className="inline">
                      {i > 0 && '. '}
                      <NarrativeIcon type={insight.icon} />
                      {insight.text}
                    </span>
                  ))}
                  <span>.</span>
                </div>
              ) : (
                <p className="text-sm text-dark-text">Insights coming soon...</p>
              )}
            </div>
          </>
        )}
      </div>

      {/* Footer accent */}
      <div
        className="h-1"
        style={{
          background: `linear-gradient(90deg, ${GOLD_ACCENT}20 0%, ${GOLD_ACCENT}60 50%, ${GOLD_ACCENT}20 100%)`,
        }}
      />
    </div>
  );
};

export default VIPMatchupPanel;
